import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { graphBuilderApi } from '../../services/api';
import './ProcessMessageContent.css';

function ProcessMessageContent({ processData, onViewSNNTrace }) {
  const [expandedSections, setExpandedSections] = useState({
    nodes: false,
    outputs: true, // Default to expanded for outputs
  });
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [reportStatus, setReportStatus] = useState(null);

  const pollReportStatus = useCallback(async () => {
    if (!processData?.execution_id) return;
    try {
      const status = await graphBuilderApi.getReportStatus(processData.execution_id);
      setReportStatus(status);
    } catch {
      // ignore
    }
  }, [processData?.execution_id]);

  useEffect(() => {
    if (processData?.status !== 'completed' && processData?.status !== 'failed') return;
    if (processData?.report_available) {
      setReportStatus({ available: true });
      return;
    }
    pollReportStatus();
    const interval = setInterval(() => {
      if (!reportStatus?.available) pollReportStatus();
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processData?.status, processData?.report_available, pollReportStatus]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return '✓';
      case 'success': return '✓';
      case 'running': return '●';
      case 'failed': return '✗';
      case 'skipped': return '○';
      case 'starting': return '◐';
      default: return '○';
    }
  };

  // Get node status
  const getNodeStatus = (nodeId) => {
    if (processData.completed_nodes?.includes(nodeId)) return 'completed';
    if (processData.active_nodes?.includes(nodeId)) return 'running';
    if (processData.failed_nodes?.includes(nodeId)) return 'failed';
    return 'pending';
  };

  // Check if node is Platform pipeline type
  const isNodeSNN = (nodeResult) => {
    return nodeResult?.snn_answer_frame || 
           nodeResult?.snn_top_outcome_nodes || 
           nodeResult?.execution_type === 'snn_pipeline';
  };

  // Get all nodes from results
  const getNodes = () => {
    if (!processData.node_results) return [];
    return Object.entries(processData.node_results).map(([nodeId, result]) => ({
      nodeId,
      ...result,
      status: result.status || getNodeStatus(nodeId),
    }));
  };

  // Deep parse any stringified JSON in the outputs
  const parseNestedJson = (obj) => {
    if (typeof obj === 'string') {
      try {
        const parsed = JSON.parse(obj);
        return parseNestedJson(parsed);
      } catch {
        return obj;
      }
    }
    if (Array.isArray(obj)) {
      return obj.map(parseNestedJson);
    }
    if (obj && typeof obj === 'object') {
      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = parseNestedJson(value);
      }
      return result;
    }
    return obj;
  };

  // Parse and clean up final outputs
  const parsedOutputs = useMemo(() => {
    if (!processData.final_outputs) return null;
    return parseNestedJson(processData.final_outputs);
  }, [processData.final_outputs]);

  const nodes = getNodes();
  const completedCount = processData.completed_nodes?.length || nodes.filter(n => n.status === 'completed' || n.status === 'success').length;
  const totalNodes = nodes.length || (processData.completed_nodes?.length + processData.pending_nodes?.length + processData.active_nodes?.length) || 0;

  return (
    <div className={`process-message-content status-${processData.status}`}>
      {/* Header */}
      <div className="process-header">
        <div className="process-title">
          <span className="process-icon">⚙️</span>
          <span className="process-name">{processData.process_name || 'Process'}</span>
        </div>
        <span className={`status-badge ${processData.status}`}>
          {processData.status?.toUpperCase()}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="progress-section">
        <div className="progress-bar-container">
          <div 
            className={`progress-bar ${processData.status}`}
            style={{ width: `${processData.progress || 0}%` }}
          />
        </div>
        <div className="progress-info">
          <span>{(processData.progress || 0).toFixed(0)}%</span>
          <span>{completedCount}/{totalNodes || '?'} nodes</span>
          <span>{formatDuration(processData.duration_ms)}</span>
          {processData.total_tokens > 0 && (
            <span>{processData.total_tokens.toLocaleString()} tokens</span>
          )}
        </div>
      </div>

      {/* Error Display */}
      {processData.error && (
        <div className="process-error">
          <strong>Error:</strong> {processData.error}
        </div>
      )}

      {/* Node Results */}
      {nodes.length > 0 && (
        <div className="section collapsible">
          <div 
            className="section-header" 
            onClick={() => toggleSection('nodes')}
          >
            <span className="section-title">Node Results ({nodes.length})</span>
            <span className="toggle-icon">{expandedSections.nodes ? '▼' : '▶'}</span>
          </div>
          {expandedSections.nodes && (
            <div className="section-content">
              <div className="nodes-list">
                {nodes.map((node) => (
                  <div 
                    key={node.nodeId} 
                    className={`node-item ${node.status} ${selectedNodeId === node.nodeId ? 'selected' : ''}`}
                    onClick={() => setSelectedNodeId(selectedNodeId === node.nodeId ? null : node.nodeId)}
                  >
                    <div className="node-header">
                      <span className={`status-icon ${node.status}`}>
                        {getStatusIcon(node.status)}
                      </span>
                      <span className="node-name">{node.node_id || node.nodeId}</span>
                      {isNodeSNN(node) && (
                        <span className="snn-badge" title="Platform Pipeline Node">Platform</span>
                      )}
                      <span className="node-metrics">
                        {node.duration_ms && <span>{formatDuration(node.duration_ms)}</span>}
                        {node.tokens_used > 0 && <span>{node.tokens_used} tok</span>}
                      </span>
                    </div>
                    
                    {/* Expanded Node Details */}
                    {selectedNodeId === node.nodeId && (
                      <div className="node-details">
                        {/* Inputs Used */}
                        {node.inputs_used && Object.keys(node.inputs_used).length > 0 && (
                          <div className="detail-section">
                            <h5>Inputs</h5>
                            <pre className="json-viewer">
                              {JSON.stringify(node.inputs_used, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {/* Outputs Produced */}
                        {node.outputs_produced && Object.keys(node.outputs_produced).length > 0 && (
                          <div className="detail-section">
                            <h5>Outputs</h5>
                            <pre className="json-viewer">
                              {JSON.stringify(node.outputs_produced, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {/* LLM Response */}
                        {node.llm_response && (
                          <div className="detail-section">
                            <h5>LLM Response</h5>
                            <div className="llm-response">{node.llm_response}</div>
                          </div>
                        )}
                        
                        {/* Platform Results */}
                        {isNodeSNN(node) && (
                          <div className="detail-section">
                            <h5>Platform Results</h5>
                            {node.snn_top_outcome_nodes && (
                              <div className="snn-outcomes">
                                <strong>Top Outcome Nodes:</strong>
                                <ul>
                                  {node.snn_top_outcome_nodes.slice(0, 5).map((n, i) => (
                                    <li key={i}>{n}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {onViewSNNTrace && (
                              <button 
                                className="btn-link"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onViewSNNTrace(node);
                                }}
                              >
                                View Platform Trace Details
                              </button>
                            )}
                          </div>
                        )}
                        
                        {/* Error */}
                        {node.error && (
                          <div className="detail-section error">
                            <h5>Error</h5>
                            <div className="error-content">
                              {node.error_type && <span className="error-type">{node.error_type}</span>}
                              <span>{node.error}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Final Outputs - Beautifully Rendered */}
      {parsedOutputs && Object.keys(parsedOutputs).length > 0 && (
        <div className="section collapsible outputs-section">
          <div 
            className="section-header" 
            onClick={() => toggleSection('outputs')}
          >
            <span className="section-title">📋 Results</span>
            <span className="toggle-icon">{expandedSections.outputs ? '▼' : '▶'}</span>
          </div>
          {expandedSections.outputs && (
            <div className="section-content outputs-content">
              <OutputRenderer data={parsedOutputs} />
              <div className="view-raw-toggle">
                <button 
                  className="btn-link-small"
                  onClick={() => setShowRawJson(!showRawJson)}
                >
                  {showRawJson ? 'Hide Raw JSON' : 'View Raw JSON'}
                </button>
              </div>
              {showRawJson && (
                <pre className="json-viewer raw-json">
                  {JSON.stringify(parsedOutputs, null, 2)}
                </pre>
              )}
            </div>
          )}
        </div>
      )}

      {/* Report Download */}
      {processData?.execution_id && (processData?.status === 'completed' || processData?.status === 'failed') && (
        <div className="report-download-row">
          {reportStatus?.available ? (
            <a
              href={graphBuilderApi.getReportDownloadUrl(processData.execution_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="report-download-link"
            >
              &#128196; Download Full Report (PDF)
            </a>
          ) : (
            <span className="report-generating-text">
              <span className="report-spinner" /> Generating report...
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// Smart output renderer that displays data in a human-readable format
function OutputRenderer({ data, depth = 0 }) {
  if (data === null || data === undefined) {
    return <span className="output-null">-</span>;
  }

  // Handle strings
  if (typeof data === 'string') {
    // Check if it's a long text (description, response, etc.)
    if (data.length > 100) {
      return <div className="output-text">{data}</div>;
    }
    return <span className="output-string">{data}</span>;
  }

  // Handle numbers and booleans
  if (typeof data === 'number' || typeof data === 'boolean') {
    return <span className="output-primitive">{String(data)}</span>;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="output-empty">Empty list</span>;
    }
    return (
      <div className="output-array">
        {data.map((item, index) => (
          <div key={index} className="output-array-item">
            <OutputRenderer data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  // Handle objects
  if (typeof data === 'object') {
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return <span className="output-empty">Empty</span>;
    }

    // Special handling for common patterns
    // Check if it looks like a prediction/house output
    const isPredictionObject = entries.some(([key]) => 
      key.includes('House') || key.includes('prediction') || key.includes('description')
    );

    // Check if it has a raw_response field
    if (data.raw_response) {
      return <OutputRenderer data={data.raw_response} depth={depth} />;
    }

    // Check if it looks like a chart prediction with houses
    if (data.chart_prediction) {
      return <OutputRenderer data={data.chart_prediction} depth={depth} />;
    }

    return (
      <div className={`output-object ${isPredictionObject ? 'prediction-style' : ''}`}>
        {entries.map(([key, value]) => {
          // Format the key nicely
          const formattedKey = key
            .replace(/_/g, ' ')
            .replace(/([A-Z])/g, ' $1')
            .replace(/^\w/, c => c.toUpperCase())
            .trim();

          // Check if value is a simple string/number or complex
          const isSimpleValue = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
          const isDescription = key.toLowerCase().includes('description') || key.toLowerCase().includes('response');
          const isDristi = key.toLowerCase().includes('dristi');

          return (
            <div key={key} className={`output-field ${isDescription ? 'description-field' : ''} ${isDristi ? 'dristi-field' : ''}`}>
              <div className="output-key">{formattedKey}</div>
              <div className="output-value">
                {isSimpleValue ? (
                  isDescription && typeof value === 'string' && value.length > 50 ? (
                    <div className="output-text">{value}</div>
                  ) : (
                    <span className={isDristi ? 'dristi-text' : ''}>{String(value)}</span>
                  )
                ) : (
                  <OutputRenderer data={value} depth={depth + 1} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return <span>{String(data)}</span>;
}

export default ProcessMessageContent;
